import type { ComplexStation } from './stationComplexIndex'

/**
 * Stop id → every stop of its complex (the stops sharing its `slug`), the
 * stop itself first, then its siblings in id order. A stop without a slug is
 * its own complex.
 *
 * The offline search starts from the stop the rider picked, but a picked stop
 * can be missing from the graph it searches: at night the map drops the
 * lines that do not run, and Oxford Circus resolves to its Bakerloo stop
 * while only Central and Victoria run (TASK-986). The search then takes the
 * first sibling the graph has. iOS / Android twin:
 * `StationComplexIndex.stops(of:)`.
 */
export function buildStationComplexStops(schema: {
  readonly stations: readonly ComplexStation[]
}): (stopId: string) => readonly string[] {
  const stopsBySlug = new Map<string, string[]>()
  for (const stop of schema.stations) {
    if (stop.slug === undefined) continue
    const stops = stopsBySlug.get(stop.slug) ?? []
    stops.push(stop.id)
    stopsBySlug.set(stop.slug, stops)
  }
  const slugByStop = new Map(schema.stations.map((stop) => [stop.id, stop.slug]))
  return (stopId) => {
    const slug = slugByStop.get(stopId)
    const siblings = slug === undefined ? [] : (stopsBySlug.get(slug) ?? [])
    return [
      stopId,
      ...siblings.filter((id) => id !== stopId).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    ]
  }
}
